import React, { useEffect, useMemo, useState } from 'react';
import moment from "moment";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import {
  getTypeLoanList,
  getAmortizationTypesList
} from "helpers/fakebackend_helper";
import { simulateLoanPayments } from "./LoanSimulator";

export interface LoanPaymentProjection {
  shareNumber: number; // Número de cuota (0 si es cuota cero)
  date: string;        // Fecha en formato 'YYYY-MM-DD'
  capital: number;     // Monto del capital pagado
  interest: number;    // Monto del interés pagado
  quota: number;       // Total a pagar en esa cuota
  balance: number;     // Saldo restante después del pago
}


const CreditSimulatorPage = () => {

  const FlatpickrComponent = Flatpickr as unknown as React.ComponentType<any>;

  const [typeLoanOptions, setTypeloanOptions] = useState<
    { value: string; label: string; interestRate: string; durationPeriod: string }[]
  >([]);

  const [amortizationTypesOptions, setAmortizationTypesOptions] = useState<
    { value: string; label: string; code: string }[]
  >([]);

  const [adminFee, setAdminFee] = useState<number>(0);

  const [disbursedAmount, setDisbursedAmount] = useState<number>(0);

  const [payments, setPayments] = useState<LoanPaymentProjection[]>([]);

  const formatCurrency = (value: number | null | undefined): string => {
    const number = typeof value === "number" ? value : 0;
    return `Q ${number.toLocaleString("es-GT", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Fetch amortization types options
  useEffect(() => {
    const fetchAmortizationTypes = async () => {
      try {
        const response = await getAmortizationTypesList();
        const embedded = response.data?._embedded?.amortizationTypes;
        const options = embedded
          ? Object.values(embedded).map((amortizationType: any) => ({
            value: amortizationType.amortizationTypeId,
            label: amortizationType.name,
            code: amortizationType.code
          }))
          : [];
        setAmortizationTypesOptions(options);
      } catch (error) {
        toast.error("Error fetching amortizationTypes: " + error, { autoClose: 2000 });
      }
    };
    fetchAmortizationTypes();
  }, []);

  useEffect(() => {
    const fetchTypeLoans = async () => {
      try {
        const response = await getTypeLoanList();
        const embedded = response.data?._embedded?.typeLoans;
        const options = embedded
          ? Object.values(embedded).map((typeLoan: any) => ({
            value: typeLoan.typeLoanId,
            label: typeLoan.name + " - " + typeLoan.interestRate + "%" + " - " + typeLoan.durationPeriod,
            interestRate: typeLoan.interestRate,
            durationPeriod: typeLoan.durationPeriod,
          }))
          : [];
        setTypeloanOptions(options);
      } catch (error) {
        toast.error("Error fetching typeLoans: " + error, { autoClose: 2000 });
      }
    };
    fetchTypeLoans();
  }, []);


  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      loanDuration: 0,
      durationPeriod: "",
      amortizationType: "",
      interestRate: 0,
      amountRequested: 0,
      loanReleasedDate: "",
      typeLoan: "",
      zeroQuota: false
    },
    validationSchema: Yup.object({
      loanDuration: Yup.number().required("Duración del préstamo es requerido"),
      durationPeriod: Yup.string().required("Periodo de duración es requerido"),
      amortizationType: Yup.string().required("Tipo de amortización es requerido"),
      typeLoan: Yup.string().required("Tipo de préstamo es requerido"),
      zeroQuota: Yup.boolean(),
      loanReleasedDate: Yup.string().nullable(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const result = simulateLoanPayments({
        principalAmount: validation.values.amountRequested,
        interestRate: validation.values.interestRate,
        loanDuration: validation.values.loanDuration,
        durationPeriod: validation.values.durationPeriod,
        amortizationCode: validation.values.amortizationType,
        zeroQuota: validation.values.zeroQuota,
        loanReleasedDate: validation.values.loanReleasedDate || new Date().toISOString().split('T')[0]
      });

      setPayments(result.payments);
      setAdminFee(result.administrativeExpenses);
      setDisbursedAmount(result.disbursedAmount);
      setSubmitting(false);
    },
  });

  return (
    <div className="card">
      <div className="card-body">
        <form onSubmit={validation.handleSubmit} className="space-y-6">
          {/* Sección: Datos del Préstamo */}
          <h4 className="text-lg font-semibold text-gray-700 dark:text-white">Datos del Préstamo</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

            {/* Tipo de Prestamo */}
            <div>
              <label htmlFor="typeLoan" className="block text-sm font-medium mb-2">
                Tipo de Préstamo <span className="text-red-500">*</span>
              </label>
              <Select
                name="typeLoan"
                options={typeLoanOptions}
                value={typeLoanOptions.find(option => option.value === validation.values.typeLoan)}
                onChange={(selectedOption: any) => {
                  validation.setFieldValue("typeLoan", selectedOption.value);
                  validation.setFieldValue("durationPeriod", selectedOption.durationPeriod);
                  validation.setFieldValue("interestRate", selectedOption.interestRate);
                }}
                onBlur={validation.handleBlur}
                className="w-full"
              />
              {validation.touched.typeLoan && validation.errors.typeLoan && (
                <p className="text-sm text-red-500 mt-1">{validation.errors.typeLoan}</p>
              )}
            </div>

            {/* Tipo de Amortización */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Tipo de Amortización <span className="text-red-500">*</span>
              </label>
              <Select
                name="amortizationType"
                options={amortizationTypesOptions}
                value={amortizationTypesOptions.find(opt => opt.value === validation.values.amortizationType)}
                onChange={(selected: any) => {
                  if (selected) {
                    validation.setFieldValue('amortizationType', selected.code);
                  }
                }}
                onBlur={validation.handleBlur}
                className="w-full"
              />
              {validation.touched.amortizationType && validation.errors.amortizationType && (
                <p className="text-sm text-red-500 mt-1">{validation.errors.amortizationType}</p>
              )}
            </div>

            {/* Fecha de Aprobación */}
            <div>
              <label htmlFor="loanReleasedDate" className="block text-sm font-medium mb-2">
                Fecha de Aprobación
              </label>
              <FlatpickrComponent
                id="loanReleasedDate"
                name="loanReleasedDate"
                placeholder="Seleccione fecha"
                options={{
                  altInput: true,
                  altFormat: "d M, Y",
                  dateFormat: "Y-m-d",
                }}
                value={validation.values.loanReleasedDate || ""}
                onChange={(date: any) =>
                  validation.setFieldValue("loanReleasedDate", moment(date[0]).format("YYYY-MM-DD"))
                }
                className="form-input w-full border rounded-md"
              />
              {validation.touched.loanReleasedDate && validation.errors.loanReleasedDate && (
                <p className="text-sm text-red-500 mt-1">{validation.errors.loanReleasedDate}</p>
              )}
            </div>

            {/* Monto Solicitado */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Monto Solicitado <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amountRequested"
                value={validation.values.amountRequested}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                placeholder="Ingrese monto"
                className="form-input w-full border rounded-md"
              />
              {validation.touched.amountRequested && validation.errors.amountRequested && (
                <p className="text-sm text-red-500 mt-1">{validation.errors.amountRequested}</p>
              )}
            </div>

            {/* Duración */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Duración del Préstamo <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="loanDuration"
                value={validation.values.loanDuration}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                placeholder="Ej. 12"
                className="form-input w-full border rounded-md"
              />
              {validation.touched.loanDuration && validation.errors.loanDuration && (
                <p className="text-sm text-red-500 mt-1">{validation.errors.loanDuration}</p>
              )}
            </div>

            {/* Periodo */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Periodo de Duración
              </label>
              <input
                type="text"
                name="durationPeriod"
                value={validation.values.durationPeriod}
                disabled
                className="form-input w-full border rounded-md bg-gray-100 dark:bg-zink-600"
              />
            </div>

            {/* Tasa de Interés */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Tasa de Interés
                <span className="ml-1 text-xs text-gray-400" title="Tasa mensual sobre saldo">ℹ️</span>
              </label>
              <input
                type="number"
                name="interestRate"
                value={validation.values.interestRate}
                disabled
                className="form-input w-full border rounded-md bg-gray-100 dark:bg-zink-600"
              />
            </div>

            {/* Cuota Cero */}
            <div className="flex items-center mt-6">
              <input
                type="checkbox"
                name="zeroQuota"
                checked={validation.values.zeroQuota}
                onChange={validation.handleChange}
                className="form-checkbox"
              />
              <label htmlFor="zeroQuota" className="ml-2 text-sm font-medium">
                Cuota Cero?
              </label>
            </div>
          </div>

          {/* Botón */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={validation.isSubmitting}
              className={`px-6 py-2 rounded-md text-white bg-custom-500 hover:bg-custom-600 transition-all ${validation.isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {validation.isSubmitting ? 'Generando...' : 'Generar Proyección'}
            </button>
          </div>
        </form>

        {payments.length > 0 && (
          <>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 border rounded-md bg-slate-50 dark:bg-zink-800">
                <p className="text-sm text-gray-600 dark:text-zink-300">Gastos Administrativos:</p>
                <p className="text-lg font-semibold text-gray-800 dark:text-white">{formatCurrency(adminFee)}</p>
              </div>
              <div className="p-4 border rounded-md bg-slate-50 dark:bg-zink-800">
                <p className="text-sm text-gray-600 dark:text-zink-300">Monto a Desembolsar:</p>
                <p className="text-lg font-semibold text-gray-800 dark:text-white">{formatCurrency(disbursedAmount)}</p>
              </div>
            </div>

            <div className="mt-8">
              <h4 className="text-base font-semibold text-gray-700 dark:text-white mb-2">Proyección de Pagos</h4>
            </div>

            <div className="overflow-x-auto border rounded-md max-h-[400px] mt-2">
              <table className="min-w-full text-sm text-left table-auto">
                <thead className="bg-gray-100 dark:bg-zink-700">
                  <tr>
                    <th className="px-4 py-2"># Cuota</th>
                    <th className="px-4 py-2">Fecha</th>
                    <th className="px-4 py-2 text-right">Saldo</th>
                    <th className="px-4 py-2 text-right">Capital</th>
                    <th className="px-4 py-2 text-right">Interés</th>
                    <th className="px-4 py-2 text-right">Cuota</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-zink-700">
                  {payments.map((pago, i) => (
                    <tr key={pago.shareNumber} className={`${i % 2 === 0 ? 'bg-white dark:bg-zink-800' : 'bg-slate-50 dark:bg-zink-700'} hover:bg-gray-50 dark:hover:bg-zink-600`}>
                      <td className="px-4 py-2">{pago.shareNumber}</td>
                      <td className="px-4 py-2">{pago.date}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(pago.balance)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(pago.capital)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(pago.interest)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(pago.quota)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );

};

export default CreditSimulatorPage;